import { MilestoneToBeBilled } from '@/services/billing/types/billing.type';
import {
  Box,
  Card,
  Stack,
  Table,
  Checkbox,
  TableRow,
  Accordion,
  TableBody,
  TableCell,
  TableHead,
  Typography,
  TableContainer,
  AccordionDetails,
  AccordionSummary,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { ChoicesOrderItemType } from '@/sections/management/accounts/orders/types';
import { ChoicesOrderItemLabel, ChoicesStatusMilestone } from '@/types';
import Label from '@components/core/label';
import { MilestoneStatusLabel } from '@components/core/milestone-status-label';
import { RHFNumberText } from '@components/forms/hook-form/rhf-number-field';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import moment from 'moment';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useBillingStore } from '../../store/billing.store';

export interface MilestoneAccordionItemProps {
  milestone: MilestoneToBeBilled;
}
export const MilestoneAccordionItem = ({ milestone }: MilestoneAccordionItemProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const selectMilestone = useBillingStore((state) => state.selectMilestone);
  const deselectMilestone = useBillingStore((state) => state.deselectMilestone);

  const isSelected = useBillingStore((state) =>
    state.selectedMilestones.some((m) => m._id === milestone._id)
  );

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, milestone: MilestoneToBeBilled) => {
      event.stopPropagation();
      if (event.target.checked) {
        selectMilestone(milestone);
      } else {
        deselectMilestone(milestone._id);
      }
    },
    [selectMilestone, deselectMilestone]
  );

  return (
    <Accordion>
      <AccordionSummary
        key={`selected-milestone-item-${milestone._id}`}
        id={`selected-milestone-item-${milestone._id}`}
        aria-controls={`selected-milestone-item-${milestone._id}`}
        expandIcon={<ExpandMoreIcon />}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography fontWeight="bold" component="span">
            {milestone.sku.name}
          </Typography>
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Card
          sx={{
            border: `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
            borderRadius: 2,
            boxShadow: theme.customShadows?.z8,
            overflow: 'hidden',
          }}
        >
          <TableContainer>
            <Table>
              <TableHead
                sx={{
                  borderBottom: `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
                  backgroundColor: 'background.neutral',
                }}
              >
                <TableRow>
                  <TableCell width={40} sx={{ py: 1.5, px: 2 }} />
                  <TableCell width={120} sx={{ py: 1.5, px: 2 }}>
                    <Typography variant="caption" fontWeight="bold">
                      {t('Billing Date')}
                    </Typography>
                  </TableCell>
                  <TableCell width={220} sx={{ py: 1.5, px: 2 }}>
                    <Typography variant="caption" fontWeight="bold">
                      {t('Product/SKU/Plan Code')}
                    </Typography>
                  </TableCell>
                  <TableCell width={140} sx={{ py: 1.5, px: 2 }}>
                    <Typography variant="caption" fontWeight="bold">
                      {t('Type')}
                    </Typography>
                  </TableCell>
                  <TableCell width={100} sx={{ py: 1.5, px: 2 }}>
                    <Typography variant="caption" fontWeight="bold">
                      {t('Amount')}
                    </Typography>
                  </TableCell>
                  <TableCell width={80} sx={{ py: 1.5, px: 2 }}>
                    <Typography variant="caption" fontWeight="bold">
                      {t('Status')}
                    </Typography>
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                <TableRow
                  sx={{
                    borderBottom: `1px solid ${alpha(theme.palette.grey[500], 0.08)}`,
                  }}
                >
                  <TableCell padding="checkbox" sx={{ py: 1.5, px: 2 }}>
                    <Checkbox
                      checked={isSelected}
                      onChange={(e) => handleCheckboxChange(e, milestone)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </TableCell>

                  <TableCell width={120} sx={{ py: 1.5, px: 2 }}>
                    <Typography variant="body2">
                      {moment(milestone.paymentDate).utc().format('L')}
                    </Typography>
                  </TableCell>

                  <TableCell width={220} sx={{ py: 1.5, px: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" noWrap>
                        {milestone.product.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {milestone.plan.name}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        {milestone.plan.code ?? '-'}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell width={140} sx={{ py: 1.5, px: 2 }}>
                    <Label
                      color={
                        milestone.orderItem?.orderItemType === ChoicesOrderItemType.newResource
                          ? 'primary'
                          : 'secondary'
                      }
                      variant="outlined"
                    >
                      {milestone.orderItem?.orderItemType
                        ? ChoicesOrderItemLabel[milestone.orderItem?.orderItemType]
                        : ChoicesOrderItemLabel.billingCloud}
                    </Label>
                  </TableCell>

                  <TableCell width={100} sx={{ py: 1.5, px: 2 }}>
                    <Typography fontWeight="bold">
                      <RHFNumberText
                        value={milestone.total}
                        decimalScale={2}
                        fixedDecimalScale
                        prefix="$"
                      />
                    </Typography>
                  </TableCell>

                  <TableCell width={80} sx={{ py: 1.5, px: 2 }}>
                    <MilestoneStatusLabel status={milestone.status as ChoicesStatusMilestone} />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </AccordionDetails>
    </Accordion>
  );
};
